/**
 * Twin table view for every chart — the a11y fallback, which is why it is mandatory.
 * Ported from `buildTable` in docs/plans/conversations/09-dashboard-mockup.html.
 *
 * Numeric (right-aligned) columns get tabular-nums so digits line up vertically —
 * the opposite call from StatTile's hero figure, which stays proportional.
 */

import type { ReactNode } from 'react';
import { Box, Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material';
import { VIZ } from './tokens';

export interface VizTableProps {
  columns: string[];
  /**
   * A cell is usually a string or a number. A node is allowed so the table can carry
   * the same affordance as the chart it mirrors (a logo, a link) — it is the a11y
   * twin, not a downgrade.
   */
  rows: ReactNode[][];
  align?: ('left' | 'right')[];
}

export default function VizTable({ columns, rows, align }: VizTableProps) {
  const alignOf = (i: number): 'left' | 'right' => align?.[i] ?? (i === 0 ? 'left' : 'right');

  return (
    <Box sx={{ maxHeight: 340, overflow: 'auto' }}>
      <Table size="small" sx={{ '& td, & th': { px: 1.25, py: 0.9 } }}>
        <TableHead>
          <TableRow>
            {columns.map((c, i) => (
              <TableCell
                key={c + i}
                align={alignOf(i)}
                sx={{
                  fontSize: 11.5,
                  textTransform: 'uppercase',
                  letterSpacing: '0.03em',
                  color: VIZ.muted,
                  fontWeight: 500,
                }}
              >
                {c}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((r, ri) => (
            <TableRow key={ri}>
              {r.map((v, i) => (
                <TableCell
                  key={i}
                  align={alignOf(i)}
                  sx={{
                    fontSize: 13,
                    color: VIZ.ink,
                    fontVariantNumeric: alignOf(i) === 'right' ? 'tabular-nums' : undefined,
                  }}
                >
                  {v}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
}
